import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2, LayoutTemplate } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { templateReportSummary } from "../lib/jobTemplates";
import {
  PageHeader, Card, Field, Input, Textarea, Select, ToggleGroup,
  PrimaryButton, QuietButton
} from "../components/UI";
import { useAuth } from "../contexts/AuthContext";
import { useJobTypeLibrary } from "../hooks/useJobTypeLibrary";
import { TemplatePreviewModal } from "../components/TemplatePreviewModal";

export default function TenderCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeTypes: jobTypes, loading: jobTypesLoading } = useJobTypeLibrary();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    site_address_line_1: "",
    site_address_line_2: "",
    site_city: "",
    site_postcode: "",
    brand: user?.preferred_brand || "lux_developments",
    job_type: ""
  });
  // LT-21b — the job type's live template is offered as a ticked checkbox so
  // the user keeps full control (untick → blank tender).
  const [templates, setTemplates] = useState([]);
  const [useTemplate, setUseTemplate] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data || [])).catch(() => setTemplates([]));
  }, []);
  const template = form.job_type ? templates.find((t) => t.job_type === form.job_type) : null;
  const templateLines = template ? (template.phases || []).reduce((n, p) => n + (p.lines || []).length, 0) : 0;

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    // LT-Estimate-UX-01 follow-up — Job Type is now MANDATORY on create.
    // It gates job-type-aware A&O seeding, the phase picker filter, and
    // the £/m² benchmark target. Making it required at create-time is
    // strictly cheaper than back-filling it later. Existing tenders
    // without a job_type are unaffected (the field remains Optional on
    // the model for reads).
    if (!form.job_type) {
      setError("Job type is required — it drives phase suggestions, A&O seeding, and £/m² benchmarks.");
      return;
    }
    if (!form.client_name || !form.site_address_line_1 || !form.site_postcode) {
      setError("Client name, address and postcode are required.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/tenders", {
        ...form,
        client_email: form.client_email || null,
        client_phone: form.client_phone || null,
        site_address_line_2: form.site_address_line_2 || null,
        site_city: form.site_city || null,
        job_type: form.job_type || null,
        apply_template: !!template && useTemplate
      });
      // LT-21b — say which job template was applied and what it loaded.
      const applied = r.data.template_applied;
      if (applied) {
        const rep = templateReportSummary(applied.lines, applied.phase_count);
        const title = `Started from "${applied.template_name}" — ${rep.text}`;
        if (rep.warnings.length) toast.warning(title, { description: rep.warnings.join(" · "), duration: 8000 });
        else toast.success(title);
      }
      navigate(`/tenders/${r.data.id}`, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create tender.");
    } finally {
      setSubmitting(false);
    }
  };

  const jobOptions = jobTypes.map((j) => ({ value: j.key, label: j.label }));

  return (
    <div className="app-container py-8">
      <PageHeader
        title="New tender"
        subtitle="Create the tender record. You can add scope, drawings and pricing once it’s opened."
        breadcrumb={
          <>
            <button onClick={() => navigate("/tenders")} className="hover:text-ink-2">Tenders</button>
            <ChevronRight size={12} />
            <span className="text-ink-2">New</span>
          </>
        }
      />

      <form onSubmit={submit}>
        <Card className="p-7">
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            {/* LT-Estimate-UX-01 follow-up — Job Type is the FIRST field
                and mandatory. It gates job-type-aware A&O seeding, the
                phase picker filter, and the £/m² benchmark target, so
                capturing it up front means every downstream feature has
                the context it needs from tender minute one. */}
            <div className="flex flex-col gap-1.5">
              <Field label="Job type" required data-testid="tender-create-job-type-field">
                <Select
                  value={form.job_type}
                  onChange={(v) => { set("job_type")(v); setUseTemplate(true); }}
                  options={jobOptions}
                  placeholder={jobTypesLoading ? "Loading…" : "Select a job type…"}
                />
              </Field>
              {form.job_type && (template ? (
                <label
                  className="flex items-start gap-2.5 mt-1 px-3 py-2.5 rounded-md border border-line bg-surface-2/60 cursor-pointer select-none"
                  data-testid="tender-create-template-option"
                >
                  <input
                    type="checkbox"
                    checked={useTemplate}
                    onChange={(e) => setUseTemplate(e.target.checked)}
                    data-testid="tender-create-use-template"
                    className="mt-0.5 accent-[#2F4F3E]"
                  />
                  <span className="flex-1 min-w-0" style={{ fontSize: 12 }}>
                    <span className="text-ink">
                      Start from the <strong>{template.name}</strong> job template
                    </span>
                    <span className="block text-ink-3 mt-0.5" style={{ fontSize: 11 }}>
                      {(template.phases || []).length} phase{(template.phases || []).length === 1 ? "" : "s"} · {templateLines} default line{templateLines === 1 ? "" : "s"} priced from the live library.
                      {" "}{useTemplate ? "Untick for a blank tender." : "Blank tender — you can apply it later from the estimate."}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); setPreviewOpen(true); }}
                    className="inline-flex items-center gap-1 text-ink-3 hover:text-accent flex-shrink-0"
                    style={{ fontSize: 11 }}
                    title="Preview the job template, priced at today's library rates"
                    data-testid="tender-create-view-template"
                  >
                    <LayoutTemplate size={11} /> View
                  </button>
                </label>
              ) : (
                <span className="text-ink-4 px-1" style={{ fontSize: 11 }} data-testid="tender-create-no-template">
                  No job template for this type yet — the tender starts blank.
                </span>
              ))}
            </div>
            <Field label="Brand" required>
              <ToggleGroup
                value={form.brand}
                onChange={set("brand")}
                options={[
                  { value: "lux_construction", label: "Lux Construction" },
                  { value: "lux_developments", label: "Lux Developments" }
                ]}
              />
            </Field>

            <Field label="Client name" required>
              <Input value={form.client_name} onChange={set("client_name")} placeholder="e.g. The Smith Family" />
            </Field>
            <div />

            <Field label="Client email">
              <Input type="email" value={form.client_email} onChange={set("client_email")} placeholder="client@example.co.uk" />
            </Field>
            <Field label="Client phone">
              <Input value={form.client_phone} onChange={set("client_phone")} placeholder="+44 …" />
            </Field>

            <Field label="Site address line 1" required>
              <Input value={form.site_address_line_1} onChange={set("site_address_line_1")} placeholder="e.g. 12 Beech Lane" />
            </Field>
            <Field label="Site address line 2">
              <Input value={form.site_address_line_2} onChange={set("site_address_line_2")} placeholder="Optional" />
            </Field>

            <Field label="Town / City">
              <Input value={form.site_city} onChange={set("site_city")} placeholder="e.g. Loughborough" />
            </Field>
            <Field label="Postcode" required>
              <Input mono value={form.site_postcode} onChange={(v) => set("site_postcode")(v.toUpperCase())} placeholder="e.g. LE11 4AB" />
            </Field>
          </div>

          {error && (
            <div
              className="mt-5 px-3 py-2 rounded-md"
              style={{ background: "#FEF3E2", color: "#7A4C0A", fontSize: 12 }}
            >
              {error}
            </div>
          )}

          <div className="mt-7 flex items-center justify-between">
            <p className="text-ink-3" style={{ fontSize: 11.5 }}>
              On creation we’ll auto-generate a tender ref (T####) and reserve a OneDrive
              folder path (real folder creation happens in a later phase).
            </p>
            <div className="flex items-center gap-2">
              <QuietButton onClick={() => navigate("/tenders")} icon={ChevronLeft}>Cancel</QuietButton>
              <PrimaryButton type="submit" disabled={submitting}>
                {submitting ? <Loader2 size={13} className="animate-spin" /> : null}
                Create tender
              </PrimaryButton>
            </div>
          </div>
        </Card>
      </form>
      <TemplatePreviewModal templateId={template?.id} open={previewOpen && !!template} onClose={() => setPreviewOpen(false)} />
    </div>
  );
}
